import { createLogger } from "../utils/logger";
import { apiCall } from "./firebase";

const logger = createLogger("ApprovalService");

export interface ApprovalStatus {
  success: boolean;
  approvalStatus: "approved" | "pending" | "rejected";
  email: string;
  displayName?: string;
  message: string;
}

export class ApprovalService {
  /**
   * Get the current user's approval status
   */
  async getApprovalStatus(): Promise<ApprovalStatus> {
    try {
      const data = await apiCall("/getUserApprovalStatus", {
        method: "GET",
      });

      logger.info("User approval status retrieved:", data);
      return data;
    } catch (error) {
      logger.error("Error getting approval status:", error);

      // Log specific error types for better debugging
      if (error instanceof Error) {
        if (error.message.includes("Not Found")) {
          logger.warn("User profile not found - possible account deletion or invalid session");
        } else if (error.message.includes("Unauthorized")) {
          logger.warn("Authentication failed - token may be expired or invalid");
        } else if (error.message.includes("Forbidden")) {
          logger.warn("Access denied - user may not have proper permissions");
        } else if (error.message.includes("API call failed")) {
          logger.warn("API endpoint failure - service may be unavailable");
        }
      }

      throw error;
    }
  }

  /**
   * Check if user is approved
   */
  async isUserApproved(): Promise<boolean> {
    try {
      const status = await this.getApprovalStatus();
      return status.approvalStatus === "approved";
    } catch (error) {
      logger.error("Error checking if user is approved:", error);
      // If we can't check status, assume not approved for security
      return false;
    }
  }

  /**
   * Get approval status with error handling
   */
  async getApprovalStatusSafe(): Promise<ApprovalStatus | null> {
    try {
      return await this.getApprovalStatus();
    } catch (error) {
      logger.error("Error getting approval status safely:", error);
      return null;
    }
  }

  /**
   * Check if an error indicates the user should be logged out
   */
  isCriticalError(error: Error): boolean {
    const errorMessage = error.message.toLowerCase();
    return (
      errorMessage.includes("not found") ||
      errorMessage.includes("api call failed") ||
      errorMessage.includes("unauthorized") ||
      errorMessage.includes("forbidden") ||
      errorMessage.includes("invalid token") ||
      errorMessage.includes("token expired")
    );
  }
}

export const approvalService = new ApprovalService();
